/* Theme custom file */

import { startViewTransition } from '@theme/utilities';

const HASH_ALIASES = ['a-venir', 'avenir', 'upcoming', 'ventes-a-venir'];

class OperationGrid extends HTMLElement {
  /** @type {AbortController} */
  #controller = new AbortController();

  /** @type {number} */
  #pageSize;

  /** @type {boolean} */
  #isHomepage;

  /** @type {number} 0 = continuous infinite scroll; N = pause after every N revealed batches */
  #pagesBeforePause = 0;

  /** @type {IntersectionObserver|null} */
  #observer = null;

  /** @type {HTMLElement|null} */
  #sentinel = null;

  /** @type {number} */
  #activeTabIndex = 0;

  /** @type {Map<number, number>} tabIndex -> visible count */
  #visibleCounts = new Map();

  /** @type {HTMLElement[]} */
  #tabs = [];

  /** @type {HTMLElement[]} */
  #cards = [];

  /** @type {HTMLElement|null} */
  #grid = null;

  /** @type {HTMLElement|null} */
  #emptyState = null;

  /** @type {HTMLElement|null} */
  #cta = null;

  /** @type {HTMLElement|null} */
  #ctaWrapper = null;

  connectedCallback() {
    this.#pageSize = parseInt(this.dataset.pageSize, 10) || 20;
    this.#isHomepage = this.dataset.isHomepage === 'true';
    this.#pagesBeforePause = parseInt(this.dataset.pagesBeforePause, 10) || 0;

    const { signal } = this.#controller;

    // Cache refs
    this.#tabs = [...this.querySelectorAll('.operation-grid__tab')];
    this.#cards = [...this.querySelectorAll('.operation-grid__card')];
    this.#grid = this.querySelector('.operation-grid__grid');
    this.#emptyState = this.querySelector('.operation-grid__empty');
    this.#ctaWrapper = this.querySelector('.operation-grid__cta');
    this.#cta = this.#ctaWrapper?.querySelector('[data-load-more]') ?? null;
    this.#sentinel = this.querySelector('.operation-grid__sentinel');

    // Tab bar
    const tabBar = this.querySelector('.operation-grid__tabs');
    if (tabBar) {
      tabBar.addEventListener('click', this.#handleTabClick, { signal });
      tabBar.addEventListener('keydown', this.#handleTabKeydown, { signal });
    }

    // CTA load more
    if (this.#cta) {
      this.#cta.addEventListener('click', this.#handleLoadMore, { signal });
    }

    // Upgrade tabs on homepage
    if (this.#isHomepage) {
      this.#upgradeTabsToARIA();
      for (const [i, tab] of this.#tabs.entries()) {
        tab.setAttribute('aria-selected', String(i === 0));
      }

      const matching = this.#tabs[0] ? this.#getMatchingCards(this.#tabs[0]) : [];
      const expectedVisible = matching.slice(0, this.#pageSize);
      const actuallyVisible = [...this.querySelectorAll('.operation-grid__card[data-visible]')];

      // The common case: server-rendered state already matches tab 0's
      // expected initial state (the first pageSize active cards visible,
      // nothing else), so there's nothing to correct. Only fall back to
      // #activateTab's hide-then-reshow cycle -- which causes a layout
      // shift, hence avoided -- when that's NOT true (e.g. active-operation
      // count below pageSize, so the shared grid_card_index counter also
      // server-marked some upcoming cards visible). Unconditionally running
      // that cycle on every load, even when redundant, forced an avoidable
      // reflow right around when the homepage's LCP element paints.
      const alreadyCorrect =
        actuallyVisible.length === expectedVisible.length &&
        expectedVisible.every((card, i) => actuallyVisible[i] === card);

      if (!alreadyCorrect) {
        this.#hideAllCards();
        this.#showCards(matching, this.#pageSize);
      }
      this.#visibleCounts.set(0, expectedVisible.length);

      const isEmpty = matching.length === 0;
      if (this.#grid) this.#grid.hidden = isEmpty;
      if (this.#emptyState) this.#emptyState.hidden = !isEmpty;

      this.#syncScroll();
    } else {
      // Seed visible count from server-rendered cards, then arm infinite scroll for the rest
      const initialVisible = this.querySelectorAll('.operation-grid__card[data-visible]').length;
      this.#visibleCounts.set(0, initialVisible);
      this.#syncScroll();
    }

    // Hash navigation
    this.#handleHashNavigation();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#observer?.disconnect();
  }

  // ── Tab upgrade (homepage only) ──

  #upgradeTabsToARIA() {
    const tabBar = this.querySelector('.operation-grid__tabs');
    if (!tabBar) return;

    tabBar.setAttribute('role', 'tablist');

    for (const [i, tab] of this.#tabs.entries()) {
      tab.setAttribute('role', 'tab');
      tab.setAttribute('tabindex', i === 0 ? '0' : '-1');
      tab.removeAttribute('aria-current');
    }
  }

  // ── Tab activation ──

  /**
   * @param {number} index
   * @param {boolean} [animate=true] Slide the indicator via a View Transition.
   */
  #activateTab(index, animate = true) {
    if (index < 0 || index >= this.#tabs.length) return;

    this.#activeTabIndex = index;
    const tab = this.#tabs[index];
    const matching = this.#getMatchingCards(tab);

    // Moving just the ARIA state inside Horizon's native View Transition makes the
    // active-tab pill (named in CSS while [data-tab-transitioning] is set) FLIP-slide
    // from the old tab to the new one, shadcn-style.
    const moveIndicator = () => {
      for (const [i, t] of this.#tabs.entries()) {
        const isActive = i === index;
        t.setAttribute('aria-selected', String(isActive));
        t.setAttribute('tabindex', isActive ? '0' : '-1');
      }
    };

    // Cards swap after the slide so they are not captured by the indicator
    // transition; they cascade in via their own reveal (--card-index stagger).
    const swapCards = () => {
      this.#hideAllCards();
      this.#showCards(matching, this.#pageSize);
      this.#visibleCounts.set(index, Math.min(this.#pageSize, matching.length));

      const isEmpty = matching.length === 0;
      if (this.#grid) this.#grid.hidden = isEmpty;
      if (this.#emptyState) this.#emptyState.hidden = !isEmpty;

      // Re-arm infinite scroll and refresh the CTA for the new tab
      this.#syncScroll();
    };

    if (animate) {
      this.setAttribute('data-tab-transitioning', '');
      startViewTransition(moveIndicator).finally(() => {
        this.removeAttribute('data-tab-transitioning');
        swapCards();
      });
    } else {
      moveIndicator();
      swapCards();
    }
  }

  /**
   * Returns cards matching a tab's filter criteria.
   * @param {HTMLElement} tab
   * @returns {HTMLElement[]}
   */
  #getMatchingCards(tab) {
    const type = tab.dataset.tabType;
    const category = tab.dataset.tabCategory;

    if (type === 'upcoming') {
      return this.#cards.filter((c) => c.dataset.status === 'upcoming');
    }

    if (type === 'category' && category) {
      return this.#cards.filter((c) => {
        const cats = c.dataset.categories;
        if (!cats) return false;
        return cats.split(',').includes(category);
      });
    }

    // ongoing -- active cards only
    return this.#cards.filter((c) => c.dataset.status === 'active');
  }

  #hideAllCards() {
    for (const card of this.#cards) {
      card.removeAttribute('data-visible');
    }
  }

  /**
   * @param {HTMLElement[]} cards
   * @param {number} count
   */
  #showCards(cards, count) {
    for (let i = 0; i < Math.min(count, cards.length); i++) {
      cards[i].style.setProperty('--card-index', String(i));
      cards[i].setAttribute('data-visible', '');
    }
  }

  // ── Infinite scroll ──

  /**
   * Cards in play for the current context: the active tab's set on the homepage,
   * the full server-rendered set on dedicated pages.
   * @returns {HTMLElement[]}
   */
  #currentMatching() {
    if (this.#isHomepage) {
      const tab = this.#tabs[this.#activeTabIndex];
      return tab ? this.#getMatchingCards(tab) : [];
    }
    return this.#cards;
  }

  /**
   * Batch (1-indexed) that a given visible count corresponds to.
   * @param {number} count
   * @returns {number}
   */
  #pageOf(count) {
    if (this.#pageSize <= 0) return count > 0 ? 1 : 0;
    return Math.ceil(count / this.#pageSize);
  }

  /** Reveal the next batch of cards for the current context. */
  #revealNext() {
    const matching = this.#currentMatching();
    if (matching.length === 0) return;

    const current = this.#visibleCounts.get(this.#activeTabIndex) ?? 0;
    if (current >= matching.length) return;

    const next = Math.min(current + this.#pageSize, matching.length);
    this.#showCards(matching, next);
    this.#visibleCounts.set(this.#activeTabIndex, next);
  }

  /**
   * Central state machine: decides whether the scroll observer is armed, whether
   * the grid is paused (manual button), or exhausted, and renders the CTA to match.
   */
  #syncScroll() {
    const matching = this.#currentMatching();
    const visibleCount = this.#visibleCounts.get(this.#activeTabIndex) ?? 0;
    const exhausted = visibleCount >= matching.length;
    const page = this.#pageOf(visibleCount);
    const paused =
      !exhausted && this.#pagesBeforePause > 0 && page % this.#pagesBeforePause === 0;

    if (exhausted || paused) {
      this.#disarmObserver();
    } else {
      this.#armObserver();
    }

    this.#renderCTA(exhausted, paused);
  }

  #armObserver() {
    if (!this.#sentinel) return;
    if (!this.#observer) {
      this.#observer = new IntersectionObserver(this.#handleIntersect, {
        rootMargin: '300px 0px',
      });
    }
    // Re-observe so a sentinel that is still on-screen re-fires and chains the next
    // batch on the following frame, filling the viewport without a manual scroll.
    this.#observer.disconnect();
    this.#observer.observe(this.#sentinel);
  }

  #disarmObserver() {
    this.#observer?.disconnect();
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #handleIntersect = (entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    this.#revealNext();
    this.#syncScroll();
  };

  // ── CTA ──

  /**
   * Renders the shared CTA element into one of three states:
   *  - paused: manual load-more button (per-tab label/url on the homepage)
   *  - exhausted: hidden on the homepage, "back to home" link on dedicated pages
   *  - continuous mid-scroll: hidden
   * @param {boolean} exhausted
   * @param {boolean} paused
   */
  #renderCTA(exhausted, paused) {
    if (!this.#ctaWrapper) return;

    if (paused) {
      this.#ctaWrapper.hidden = false;
      if (this.#cta) {
        this.#cta.setAttribute('data-load-more', '');
        if (this.#isHomepage) {
          const tab = this.#tabs[this.#activeTabIndex];
          const label = tab?.dataset.tabCtaLabel;
          const url = tab?.dataset.tabCtaUrl;
          if (label) this.#cta.textContent = label;
          if (url) this.#cta.href = url;
        }
      }
      return;
    }

    if (exhausted) {
      if (this.#isHomepage) {
        this.#ctaWrapper.hidden = true;
      } else {
        // Dedicated page -- swap the button to a back-to-home link (href already root_url)
        this.#ctaWrapper.hidden = false;
        if (this.#cta) {
          this.#cta.removeAttribute('data-load-more');
          const homeLabel = this.dataset.homeCtaLabel;
          if (homeLabel) this.#cta.textContent = homeLabel;
        }
      }
      return;
    }

    // Continuous infinite scroll mid-list -- no button
    this.#ctaWrapper.hidden = true;
  }

  // ── Event handlers ──

  /** Tab types that always navigate (no client-side filter): date-based tabs and redirect links */
  static #NAV_ONLY_TABS = new Set(['new_sales', 'last_hours', 'link']);

  /** @param {MouseEvent} event */
  #handleTabClick = (event) => {
    const tab = event.target.closest('.operation-grid__tab');
    if (!tab) return;

    // Date-based tabs always navigate (even on homepage)
    if (OperationGrid.#NAV_ONLY_TABS.has(tab.dataset.tabType)) return;

    // On homepage, intercept and filter client-side
    if (this.#isHomepage) {
      event.preventDefault();
      const index = this.#tabs.indexOf(tab);
      if (index !== -1) this.#activateTab(index);
    }
    // On dedicated pages, let the link navigate
  };

  /** @param {KeyboardEvent} event */
  #handleTabKeydown = (event) => {
    if (!this.#isHomepage) return;

    const currentIndex = this.#tabs.indexOf(document.activeElement);
    if (currentIndex === -1) return;

    let nextIndex;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % this.#tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + this.#tabs.length) % this.#tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.#tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextTab = this.#tabs[nextIndex];

    // Nav-only tabs (new_sales / last_hours / link) are links, not client-side
    // filters, so move roving focus onto them without activating/filtering
    // (mirrors #handleTabClick, which also skips NAV_ONLY_TABS). Enter/Space
    // still follows the link.
    if (OperationGrid.#NAV_ONLY_TABS.has(nextTab.dataset.tabType)) {
      for (const [i, tab] of this.#tabs.entries()) {
        tab.setAttribute('tabindex', i === nextIndex ? '0' : '-1');
      }
    } else {
      this.#activateTab(nextIndex);
    }
    nextTab.focus();
  };

  /** @param {MouseEvent} event */
  #handleLoadMore = (event) => {
    // Home-link mode (dedicated page with all cards revealed) -- let navigation happen
    if (!this.#cta?.hasAttribute('data-load-more')) return;

    event.preventDefault();

    // Reveal one batch, then re-arm scroll (continuous until the next pause boundary)
    this.#revealNext();
    this.#syncScroll();
  };

  // ── Hash navigation ──

  #handleHashNavigation() {
    const hash = location.hash.replace('#', '');
    if (!hash || !HASH_ALIASES.includes(hash)) return;

    const target = this.querySelector('#a-venir');
    if (!target) return;

    // On homepage, the target lives under the upcoming tab -- activate it first
    if (this.#isHomepage) {
      const upcomingIndex = this.#tabs.findIndex((t) => t.dataset.tabType === 'upcoming');
      if (upcomingIndex !== -1 && upcomingIndex !== this.#activeTabIndex) {
        this.#activateTab(upcomingIndex);
      }
    }

    // Homepage filters per active tab; dedicated pages use the already-filtered server-rendered set
    const tab = this.#tabs[this.#activeTabIndex];
    const matching = this.#isHomepage && tab ? this.#getMatchingCards(tab) : this.#cards;
    const targetIndex = matching.indexOf(target);
    if (targetIndex === -1) return;

    const needed = targetIndex + 1;
    const currentCount = this.#visibleCounts.get(this.#activeTabIndex) ?? 0;

    if (needed > currentCount) {
      const batchesNeeded = Math.ceil(needed / this.#pageSize);
      const revealCount = Math.min(batchesNeeded * this.#pageSize, matching.length);

      this.#showCards(matching, revealCount);
      this.#visibleCounts.set(this.#activeTabIndex, revealCount);

      // Re-sync scroll/CTA state around the newly revealed batches
      this.#syncScroll();
    }

    // Scroll to the target after a frame to ensure layout has updated.
    // Reveal above happens synchronously, so the anchor scroll always fires
    // regardless of the infinite-scroll pause state.
    requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }
}

customElements.define('operation-grid', OperationGrid);
